// Performance monitoring utilities for lazy loading optimization
import React from 'react';

interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, any>;
}

interface BundleInfo {
  name: string;
  size?: number;
  loadTime: number;
  cached: boolean;
}

class PerformanceMonitor {
  private metrics = new Map<string, PerformanceMetric>();
  private bundles = new Map<string, BundleInfo>();
  private isEnabled: boolean;

  constructor() {
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  /**
   * Start timing a performance metric
   */
  start(name: string, metadata?: Record<string, any>): void {
    if (!this.isEnabled) return;

    this.metrics.set(name, {
      name,
      startTime: performance.now(),
      ...(metadata && { metadata }),
    });
  }

  /**
   * End timing a performance metric
   */
  end(name: string, metadata?: Record<string, any>): number | undefined {
    if (!this.isEnabled) return;

    const metric = this.metrics.get(name);
    if (!metric) {
      console.warn(`PerformanceMonitor: No start time recorded for '${name}'`);
      return;
    }

    const endTime = performance.now();
    const duration = endTime - metric.startTime;

    metric.endTime = endTime;
    metric.duration = duration;
    metric.metadata = { ...metric.metadata, ...metadata };

    this.logMetric(metric);
    return duration;
  }

  /**
   * Record a bundle loading event
   */
  recordBundle(name: string, info: Partial<BundleInfo>): void {
    if (!this.isEnabled) return;

    this.bundles.set(name, {
      name,
      loadTime: performance.now(),
      cached: false,
      ...info,
    });

    console.debug(`Bundle loaded: ${name}`, info);
  }

  /**
   * Measure component lazy loading
   */
  measureLazyLoad = <T extends React.ComponentType<any>>(
    componentName: string,
    loader: () => Promise<{ default: T }>
  ) => {
    return async (): Promise<{ default: T }> => {
      this.start(`lazy-load-${componentName}`);

      try {
        const result = await loader();

        this.end(`lazy-load-${componentName}`, {
          component: componentName,
          success: true,
        });

        return result;
      } catch (error) {
        this.end(`lazy-load-${componentName}`, {
          component: componentName,
          success: false,
          error: (error as Error).message,
        });
        throw error;
      }
    };
  };

  /**
   * Measure chart library loading
   */
  measureChartLoad = <T>(
    libraryName: string,
    loader: () => Promise<T>
  ) => {
    return async (): Promise<T> => {
      this.start(`chart-load-${libraryName}`);

      try {
        const result = await loader();

        this.end(`chart-load-${libraryName}`, {
          library: libraryName,
          success: true,
        });

        return result;
      } catch (error) {
        this.end(`chart-load-${libraryName}`, {
          library: libraryName,
          success: false,
          error: (error as Error).message,
        });
        throw error;
      }
    };
  };

  /**
   * Get performance stats
   */
  getStats() {
    if (!this.isEnabled) return null;

    const completedMetrics = Array.from(this.metrics.values())
      .filter(metric => metric.duration !== undefined);

    const bundleStats = Array.from(this.bundles.values());

    return {
      totalMetrics: completedMetrics.length,
      averageLoadTime: completedMetrics.length > 0
        ? completedMetrics.reduce((sum, metric) => sum + (metric.duration || 0), 0) / completedMetrics.length
        : 0,
      bundles: bundleStats,
      slowestOperations: completedMetrics
        .sort((a, b) => (b.duration || 0) - (a.duration || 0))
        .slice(0, 5),
    };
  }

  /**
   * Log metric to console in development
   */
  private logMetric(metric: PerformanceMetric): void {
    if (!this.isEnabled || !metric.duration) return;

    const style = metric.duration > 1000 ? 'color: red' :
                  metric.duration > 500 ? 'color: orange' :
                  'color: green';

    console.debug(
      `%c⚡ ${metric.name}: ${metric.duration.toFixed(2)}ms`,
      style,
      metric.metadata
    );
  }

  /**
   * Clear all metrics (for testing)
   */
  clear(): void {
    this.metrics.clear();
    this.bundles.clear();
  }

  /**
   * Export metrics for analysis
   */
  export(): string {
    if (!this.isEnabled) return '{}';

    return JSON.stringify({
      metrics: Array.from(this.metrics.values()),
      bundles: Array.from(this.bundles.values()),
      timestamp: new Date().toISOString(),
    }, null, 2);
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * Hook for component-level performance monitoring
 */
export const usePerformanceMonitor = () => {
  const start = (name: string, metadata?: Record<string, any>) => {
    performanceMonitor.start(name, metadata);
  };

  const end = (name: string, metadata?: Record<string, any>) => {
    return performanceMonitor.end(name, metadata);
  };

  const getStats = () => {
    return performanceMonitor.getStats();
  };

  return { start, end, getStats };
};

/**
 * Higher-order component for measuring component render time
 */
export const withPerformanceTracking = <P extends {}>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string
) => {
  return React.forwardRef<any, P>((props, ref) => {
    React.useEffect(() => {
      performanceMonitor.start(`render-${componentName}`);

      return () => {
        performanceMonitor.end(`render-${componentName}`);
      };
    }, []);

    return React.createElement(WrappedComponent, { ...props, ref } as any);
  });
};

export default performanceMonitor;